import { createCanvas } from './loadImage';
import { sampleTritoneColor, getPerceivedLuminance, parseHexColor } from './composite';
import type { MotionColorMatteChannel } from '@motionpitch/template-contract';

/**
 * Puzzle Matte recolouring.
 *
 * An AOV pass rendered alongside the atlas paints each user-editable region into
 * one of the RGB channels. Here that mask is read back and those regions are
 * re-tinted with the tritone the static templates use for background colour:
 * black shadow, white highlight, the picked colour in the middle.
 *
 * Three properties matter more than the effect itself:
 *
 * 1. A template without a matte, or with the feature switched off, must take a
 *    byte-identical path to the one it took before this file existed. Every
 *    entry point below returns null in that case and the compositor draws
 *    exactly what it always drew.
 * 2. The result is an overlay, not a replacement. The compositor paints it after
 *    the diffuse adjustments, so the Color Control sliders cannot reach a region
 *    the viewer has recoloured — the picked colour is the last word.
 * 3. Tinting works best on the neutral atlas, where the recolourable objects are
 *    already grey. Without one the tint lands on the object's own hue and the
 *    result drifts away from the swatch; the caller decides which atlas to feed.
 */

export interface MotionColorMatteAdjustments {
    /** Master switch. Off means the template renders exactly as it would without a matte. */
    enabled: boolean;
    /** Picked colour per channel. A missing entry leaves that region alone. */
    colors: Partial<Record<MotionColorMatteChannel, string>>;
    /**
     * Background tint, driven by its own black-and-white matte and its own
     * switch. Two points rather than three: the background is a backdrop, and a
     * dark-to-light ramp between two picked colours is what reads correctly on
     * one.
     *
     * Independent of `enabled`. The master switch governs the object colours and
     * the neutral atlas they need; the background needs neither, so tying it to
     * the master would only force a viewer who wants a different backdrop to
     * recolour the objects as well. Off by default.
     */
    background: {
        enabled: boolean;
        dark: string;
        light: string;
    };
}

/** Whether the background tint would change anything right now. */
export function isBackgroundTintActive(
    hasMatte: boolean,
    adjustments: MotionColorMatteAdjustments | null | undefined
): boolean {
    return Boolean(hasMatte && adjustments?.background.enabled);
}

type Rgb = { r: number; g: number; b: number };

function mixRgb(from: Rgb, to: Rgb, amount: number): Rgb {
    const t = Math.max(0, Math.min(1, amount));
    return {
        r: from.r + (to.r - from.r) * t,
        g: from.g + (to.g - from.g) * t,
        b: from.b + (to.b - from.b) * t
    };
}

const SHADOW = { r: 0, g: 0, b: 0 };
const HIGHLIGHT = { r: 255, g: 255, b: 255 };

const CHANNEL_OFFSET: Record<MotionColorMatteChannel, number> = { r: 0, g: 1, b: 2 };

/**
 * Channels that will actually change pixels: the master switch is on, the
 * template declares them, and a colour has been picked. Anything else is dropped
 * here so the render path never has to re-check it.
 */
export function resolveActiveMatteChannels(
    declared: readonly MotionColorMatteChannel[] | null | undefined,
    adjustments: MotionColorMatteAdjustments | null | undefined
): MotionColorMatteChannel[] {
    if (!declared || declared.length === 0 || !adjustments || !adjustments.enabled) {
        return [];
    }
    return declared.filter((channel) => {
        const color = adjustments.colors[channel];
        return typeof color === 'string' && color.trim().length > 0;
    });
}

// Reused across frames. This runs once per rendered frame, and allocating two
// canvases each time is enough to stutter playback on mobile.
let _matteScratch: HTMLCanvasElement | null = null;
let _backgroundScratch: HTMLCanvasElement | null = null;
let _overlayScratch: HTMLCanvasElement | null = null;

export type MotionColorMatteCompositorBackend = 'gpu' | 'cpu' | 'none';

let _lastCompositorBackend: MotionColorMatteCompositorBackend = 'none';

export function getMotionColorMatteCompositorBackend(): MotionColorMatteCompositorBackend {
    return _lastCompositorBackend;
}

type GpuColorMatteState = {
    canvas: HTMLCanvasElement;
    gl: WebGLRenderingContext;
    program: WebGLProgram;
    positionBuffer: WebGLBuffer;
    textures: [WebGLTexture, WebGLTexture, WebGLTexture];
    textureUploads: [GpuTextureUpload | null, GpuTextureUpload | null, GpuTextureUpload | null];
    attribPosition: number;
    uniforms: {
        base: WebGLUniformLocation;
        matte: WebGLUniformLocation;
        background: WebGLUniformLocation;
        channelEnabled: WebGLUniformLocation;
        redColor: WebGLUniformLocation;
        greenColor: WebGLUniformLocation;
        blueColor: WebGLUniformLocation;
        backgroundEnabled: WebGLUniformLocation;
        backgroundDark: WebGLUniformLocation;
        backgroundLight: WebGLUniformLocation;
    };
};

type GpuTextureUpload = {
    width: number;
    height: number;
    transparent: boolean;
};

let _gpuColorMatteState: GpuColorMatteState | null | undefined;
const TRANSPARENT_PIXEL = new Uint8Array([0, 0, 0, 0]);

function compileGpuShader(
    gl: WebGLRenderingContext,
    type: number,
    source: string
): WebGLShader | null {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader);
        return null;
    }
    return shader;
}

function getGpuColorMatteState(width: number, height: number): GpuColorMatteState | null {
    if (_gpuColorMatteState === null) return null;
    if (_gpuColorMatteState) return _gpuColorMatteState;
    if (typeof document === 'undefined') {
        _gpuColorMatteState = null;
        return null;
    }

    const canvas = createCanvas(width, height);
    const gl = canvas.getContext('webgl', {
        alpha: true,
        antialias: false,
        depth: false,
        stencil: false,
        premultipliedAlpha: false,
        preserveDrawingBuffer: true
    });
    if (!gl) {
        _gpuColorMatteState = null;
        return null;
    }

    const vertexShader = compileGpuShader(
        gl,
        gl.VERTEX_SHADER,
        `
            attribute vec2 a_position;
            varying vec2 v_uv;

            void main() {
                gl_Position = vec4(a_position, 0.0, 1.0);
                v_uv = a_position * 0.5 + 0.5;
            }
        `
    );
    const fragmentShader = compileGpuShader(
        gl,
        gl.FRAGMENT_SHADER,
        `
            precision highp float;

            uniform sampler2D u_base;
            uniform sampler2D u_matte;
            uniform sampler2D u_background;
            uniform vec3 u_channelEnabled;
            uniform vec3 u_redColor;
            uniform vec3 u_greenColor;
            uniform vec3 u_blueColor;
            uniform float u_backgroundEnabled;
            uniform vec3 u_backgroundDark;
            uniform vec3 u_backgroundLight;
            varying vec2 v_uv;

            float channelCoverage(float own, float rivalA, float rivalB) {
                float rival = max(rivalA, rivalB);
                float headroom = 1.0 - rival;
                if (headroom <= 0.0) return 0.0;
                return clamp((own - rival) / headroom, 0.0, 1.0);
            }

            vec3 tritone(vec3 middle, float luminance) {
                if (luminance <= 0.5) {
                    return mix(vec3(0.0), middle, luminance * 2.0);
                }
                return mix(middle, vec3(1.0), (luminance - 0.5) * 2.0);
            }

            void addTint(
                inout vec3 accumulated,
                inout float weight,
                vec3 tint,
                float coverage
            ) {
                accumulated += tint * coverage;
                weight += coverage;
            }

            void main() {
                vec4 base = texture2D(u_base, v_uv);
                if (base.a <= 0.0) {
                    gl_FragColor = vec4(0.0);
                    return;
                }

                vec3 matte = texture2D(u_matte, v_uv).rgb;
                vec3 background = texture2D(u_background, v_uv).rgb;
                float luminance = clamp(
                    dot(base.rgb, vec3(0.2126, 0.7152, 0.0722)),
                    0.0,
                    1.0
                );
                vec3 accumulated = vec3(0.0);
                float weight = 0.0;

                float backgroundCoverage = background.g * u_backgroundEnabled;
                if (backgroundCoverage > 0.0) {
                    addTint(
                        accumulated,
                        weight,
                        mix(u_backgroundDark, u_backgroundLight, luminance),
                        backgroundCoverage
                    );
                }

                float redCoverage = channelCoverage(matte.r, matte.g, matte.b) * u_channelEnabled.r;
                if (redCoverage > 0.0) {
                    addTint(accumulated, weight, tritone(u_redColor, luminance), redCoverage);
                }
                float greenCoverage = channelCoverage(matte.g, matte.r, matte.b) * u_channelEnabled.g;
                if (greenCoverage > 0.0) {
                    addTint(accumulated, weight, tritone(u_greenColor, luminance), greenCoverage);
                }
                float blueCoverage = channelCoverage(matte.b, matte.r, matte.g) * u_channelEnabled.b;
                if (blueCoverage > 0.0) {
                    addTint(accumulated, weight, tritone(u_blueColor, luminance), blueCoverage);
                }

                if (weight <= 0.0) {
                    gl_FragColor = vec4(0.0);
                    return;
                }

                gl_FragColor = vec4(accumulated / weight, base.a * min(1.0, weight));
            }
        `
    );
    if (!vertexShader || !fragmentShader) {
        _gpuColorMatteState = null;
        return null;
    }

    const program = gl.createProgram();
    if (!program) {
        _gpuColorMatteState = null;
        return null;
    }
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        gl.deleteProgram(program);
        _gpuColorMatteState = null;
        return null;
    }

    const positionBuffer = gl.createBuffer();
    const textures = [gl.createTexture(), gl.createTexture(), gl.createTexture()] as const;
    const attribPosition = gl.getAttribLocation(program, 'a_position');
    const uniform = (name: string) => gl.getUniformLocation(program, name);
    const uniforms = {
        base: uniform('u_base'),
        matte: uniform('u_matte'),
        background: uniform('u_background'),
        channelEnabled: uniform('u_channelEnabled'),
        redColor: uniform('u_redColor'),
        greenColor: uniform('u_greenColor'),
        blueColor: uniform('u_blueColor'),
        backgroundEnabled: uniform('u_backgroundEnabled'),
        backgroundDark: uniform('u_backgroundDark'),
        backgroundLight: uniform('u_backgroundLight')
    };
    if (
        !positionBuffer ||
        textures.some((texture) => !texture) ||
        attribPosition < 0 ||
        Object.values(uniforms).some((location) => location === null)
    ) {
        _gpuColorMatteState = null;
        return null;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([
            -1, -1,
            1, -1,
            -1, 1,
            -1, 1,
            1, -1,
            1, 1
        ]),
        gl.STATIC_DRAW
    );
    for (const texture of textures) {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    }

    _gpuColorMatteState = {
        canvas,
        gl,
        program,
        positionBuffer,
        textures: textures as [WebGLTexture, WebGLTexture, WebGLTexture],
        textureUploads: [null, null, null],
        attribPosition,
        uniforms: uniforms as GpuColorMatteState['uniforms']
    };
    return _gpuColorMatteState;
}

/**
 * Compiles the Puzzle Matte WebGL program before the first interactive toggle.
 * Doing this while the Motion assets are being prepared keeps shader setup out
 * of the live playback frame where it would otherwise look like a short freeze.
 */
export function warmMotionColorMatteCompositor(): void {
    if (_gpuColorMatteState === undefined) {
        getGpuColorMatteState(1, 1);
    }
}

function setGpuTexture(
    state: GpuColorMatteState,
    unit: number,
    source: CanvasImageSource | null
): void {
    const { gl } = state;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, state.textures[unit]);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    if (source) {
        const dimensions = getGpuTextureSourceDimensions(source);
        const previous = state.textureUploads[unit];
        if (
            dimensions &&
            previous &&
            !previous.transparent &&
            previous.width === dimensions.width &&
            previous.height === dimensions.height
        ) {
            // Storage is stable across video frames. Updating it in place avoids
            // forcing Chromium's GPU process to redefine a 1920x1920 texture on
            // every preview tick.
            gl.texSubImage2D(
                gl.TEXTURE_2D,
                0,
                0,
                0,
                gl.RGBA,
                gl.UNSIGNED_BYTE,
                source as TexImageSource
            );
        } else {
            gl.texImage2D(
                gl.TEXTURE_2D,
                0,
                gl.RGBA,
                gl.RGBA,
                gl.UNSIGNED_BYTE,
                source as TexImageSource
            );
        }
        state.textureUploads[unit] = dimensions
            ? { ...dimensions, transparent: false }
            : null;
    } else {
        const previous = state.textureUploads[unit];
        if (previous?.transparent) {
            return;
        }
        gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            gl.RGBA,
            1,
            1,
            0,
            gl.RGBA,
            gl.UNSIGNED_BYTE,
            TRANSPARENT_PIXEL
        );
        state.textureUploads[unit] = { width: 1, height: 1, transparent: true };
    }
}

function getGpuTextureSourceDimensions(
    source: CanvasImageSource
): { width: number; height: number } | null {
    const candidate = source as CanvasImageSource & {
        videoWidth?: number;
        videoHeight?: number;
        naturalWidth?: number;
        naturalHeight?: number;
        width?: number;
        height?: number;
    };
    const width = Number(candidate.videoWidth || candidate.naturalWidth || candidate.width || 0);
    const height = Number(candidate.videoHeight || candidate.naturalHeight || candidate.height || 0);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return null;
    }
    return { width: Math.round(width), height: Math.round(height) };
}

function normalizeColor(color: Rgb): [number, number, number] {
    return [color.r / 255, color.g / 255, color.b / 255];
}

function tryBuildMotionColorMatteOverlayGpu(
    baseTile: HTMLCanvasElement,
    matteSource: CanvasImageSource | null,
    backgroundSource: CanvasImageSource | null,
    channels: readonly MotionColorMatteChannel[],
    adjustments: MotionColorMatteAdjustments
): HTMLCanvasElement | null {
    const width = baseTile.width;
    const height = baseTile.height;
    const state = getGpuColorMatteState(width, height);
    if (!state || state.gl.isContextLost()) return null;

    const { gl, uniforms } = state;
    try {
        if (state.canvas.width !== width || state.canvas.height !== height) {
            state.canvas.width = width;
            state.canvas.height = height;
        }
        gl.viewport(0, 0, width, height);
        gl.disable(gl.BLEND);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.useProgram(state.program);
        gl.bindBuffer(gl.ARRAY_BUFFER, state.positionBuffer);
        gl.enableVertexAttribArray(state.attribPosition);
        gl.vertexAttribPointer(state.attribPosition, 2, gl.FLOAT, false, 0, 0);

        setGpuTexture(state, 0, baseTile);
        setGpuTexture(state, 1, channels.length > 0 ? matteSource : null);
        setGpuTexture(state, 2, backgroundSource);
        gl.uniform1i(uniforms.base, 0);
        gl.uniform1i(uniforms.matte, 1);
        gl.uniform1i(uniforms.background, 2);

        const channelEnabled: [number, number, number] = [0, 0, 0];
        const channelColors: Record<MotionColorMatteChannel, [number, number, number]> = {
            r: [0, 0, 0],
            g: [0, 0, 0],
            b: [0, 0, 0]
        };
        channels.forEach((channel) => {
            channelEnabled[CHANNEL_OFFSET[channel]] = 1;
            channelColors[channel] = normalizeColor(parseHexColor(adjustments.colors[channel]!));
        });
        gl.uniform3fv(uniforms.channelEnabled, channelEnabled);
        gl.uniform3fv(uniforms.redColor, channelColors.r);
        gl.uniform3fv(uniforms.greenColor, channelColors.g);
        gl.uniform3fv(uniforms.blueColor, channelColors.b);

        const backgroundEnabled = backgroundSource ? 1 : 0;
        gl.uniform1f(uniforms.backgroundEnabled, backgroundEnabled);
        gl.uniform3fv(uniforms.backgroundDark, normalizeColor(parseHexColor(adjustments.background.dark)));
        gl.uniform3fv(uniforms.backgroundLight, normalizeColor(parseHexColor(adjustments.background.light)));
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        return state.canvas;
    } catch {
        return null;
    }
}

function getScratch(
    current: HTMLCanvasElement | null,
    width: number,
    height: number
): HTMLCanvasElement {
    if (current && current.width === width && current.height === height) {
        return current;
    }
    return createCanvas(width, height);
}

/**
 * Builds the recoloured regions as a standalone layer, transparent everywhere
 * the matte does not reach, or null when there is nothing to recolour.
 *
 * The base tile is only read, never written: it belongs to the atlas tile cache,
 * and tinting it in place would bake one frame's colours into every later frame.
 */
function buildMotionColorMatteOverlayCpu(
    baseTile: CanvasImageSource,
    sources: {
        matte: CanvasImageSource | null;
        backgroundMatte?: CanvasImageSource | null;
    },
    declared: readonly MotionColorMatteChannel[] | null | undefined,
    adjustments: MotionColorMatteAdjustments | null | undefined
): HTMLCanvasElement | null {
    const matteSource = sources.matte;
    const channels = matteSource ? resolveActiveMatteChannels(declared, adjustments) : [];
    const backgroundSource = isBackgroundTintActive(Boolean(sources.backgroundMatte), adjustments)
        ? sources.backgroundMatte || null
        : null;

    if (channels.length === 0 && !backgroundSource) {
        return null;
    }

    // Recolouring reads pixels back, which needs a canvas. Atlas tiles always
    // are one; anything else is left alone rather than guessed at.
    if (!(baseTile instanceof HTMLCanvasElement)) {
        return null;
    }

    const width = baseTile.width;
    const height = baseTile.height;
    if (width <= 0 || height <= 0) {
        return null;
    }

    const baseCtx = baseTile.getContext('2d');
    if (!baseCtx) {
        return null;
    }

    // The matte is a standalone video, not an atlas tile, so its resolution is
    // whatever the author exported. Scaling it to the tile keeps preview (640)
    // and export (1920) on the same footing.
    // A frame that cannot be drawn (not yet decoded, tainted, closed) must not
    // take the template down with it: that source is simply skipped.
    const readMatte = (
        source: CanvasImageSource | null,
        scratch: HTMLCanvasElement | null
    ): { pixels: Uint8ClampedArray; scratch: HTMLCanvasElement } | null => {
        if (!source) {
            return null;
        }
        const canvas = getScratch(scratch, width, height);
        const context = canvas.getContext('2d');
        if (!context) {
            return null;
        }
        context.clearRect(0, 0, width, height);
        try {
            context.drawImage(source, 0, 0, width, height);
            return { pixels: context.getImageData(0, 0, width, height).data, scratch: canvas };
        } catch {
            return null;
        }
    };

    const matteRead = readMatte(channels.length > 0 ? matteSource : null, _matteScratch);
    if (matteRead) _matteScratch = matteRead.scratch;
    const backgroundRead = readMatte(backgroundSource, _backgroundScratch);
    if (backgroundRead) _backgroundScratch = backgroundRead.scratch;

    if (!matteRead && !backgroundRead) {
        return null;
    }

    let basePixels: Uint8ClampedArray;
    try {
        basePixels = baseCtx.getImageData(0, 0, width, height).data;
    } catch {
        return null;
    }
    const mattePixels = matteRead?.pixels;
    const backgroundPixels = backgroundRead?.pixels;

    _overlayScratch = getScratch(_overlayScratch, width, height);
    const overlayCtx = _overlayScratch.getContext('2d');
    if (!overlayCtx) {
        return null;
    }
    const overlay = overlayCtx.createImageData(width, height);
    const outPixels = overlay.data;

    const tints = channels.map((channel) => {
        const offset = CHANNEL_OFFSET[channel];
        return {
            offset,
            // The two channels this one competes with, for the crosstalk rescale below.
            rivals: [0, 1, 2].filter((candidate) => candidate !== offset),
            color: parseHexColor(adjustments!.colors[channel]!)
        };
    });

    let painted = false;

    const backgroundDark = backgroundPixels ? parseHexColor(adjustments!.background.dark) : null;
    const backgroundLight = backgroundPixels ? parseHexColor(adjustments!.background.light) : null;

    for (let index = 0; index < outPixels.length; index += 4) {
        const baseAlpha = basePixels[index + 3];
        if (baseAlpha === 0) {
            continue;
        }

        const luminance = getPerceivedLuminance(
            basePixels[index],
            basePixels[index + 1],
            basePixels[index + 2]
        );

        let r = 0;
        let g = 0;
        let b = 0;
        let weight = 0;

        // Background first: it is the backdrop, and an object marked in the colour
        // matte paints over it in the same pass.
        if (backgroundPixels && backgroundDark && backgroundLight) {
            // The matte is black and white, so any channel carries the same value;
            // reading the green one is the least noisy after chroma subsampling.
            const coverage = backgroundPixels[index + 1] / 255;
            if (coverage > 0) {
                // Two points, not three: a backdrop reads as a dark-to-light ramp
                // between the picked pair rather than as shadow/mid/highlight.
                const tinted = mixRgb(backgroundDark, backgroundLight, luminance);
                r += tinted.r * coverage;
                g += tinted.g * coverage;
                b += tinted.b * coverage;
                weight += coverage;
            }
        }

        for (let t = 0; t < tints.length; t += 1) {
            const tint = tints[t];
            // Chroma subsampling bleeds a little of each region into the other
            // channels — a red-marked area measured 26/255 of green. Rescaling
            // against the strongest rival removes that bleed without weakening
            // the channel that owns the pixel: the winner still reaches full
            // strength, the loser lands on zero, and a genuine soft edge is
            // untouched because there the rival channels are already zero.
            // A pixel two channels claim equally is left alone rather than guessed at.
            const rival = Math.max(mattePixels![index + tint.rivals[0]], mattePixels![index + tint.rivals[1]]);
            const headroom = 255 - rival;
            const coverage =
                headroom <= 0
                    ? 0
                    : Math.min(1, Math.max(0, (mattePixels![index + tint.offset] - rival) / headroom));
            if (coverage <= 0) {
                continue;
            }

            const tinted = sampleTritoneColor(SHADOW, tint.color, HIGHLIGHT, luminance);
            r += tinted.r * coverage;
            g += tinted.g * coverage;
            b += tinted.b * coverage;
            weight += coverage;
        }

        if (weight <= 0) {
            continue;
        }

        // Colours are accumulated weighted so two regions overlapping in a blurred
        // edge mix rather than one of them simply winning.
        const total = Math.min(1, weight);
        outPixels[index] = Math.round(r / weight);
        outPixels[index + 1] = Math.round(g / weight);
        outPixels[index + 2] = Math.round(b / weight);
        outPixels[index + 3] = Math.round(baseAlpha * total);
        painted = true;
    }

    if (!painted) {
        return null;
    }

    overlayCtx.putImageData(overlay, 0, 0);
    return _overlayScratch;
}

/**
 * Uses the GPU for the per-pixel Puzzle Matte pass. The previous CPU compositor
 * remains the compatibility fallback, so browsers without a usable WebGL
 * context keep the same output instead of losing enabled colour layers.
 */
export function buildMotionColorMatteOverlay(
    baseTile: CanvasImageSource,
    sources: {
        matte: CanvasImageSource | null;
        backgroundMatte?: CanvasImageSource | null;
    },
    declared: readonly MotionColorMatteChannel[] | null | undefined,
    adjustments: MotionColorMatteAdjustments | null | undefined
): HTMLCanvasElement | null {
    const matteSource = sources.matte;
    const channels = matteSource ? resolveActiveMatteChannels(declared, adjustments) : [];
    const backgroundSource = isBackgroundTintActive(Boolean(sources.backgroundMatte), adjustments)
        ? sources.backgroundMatte || null
        : null;
    if (channels.length === 0 && !backgroundSource) {
        _lastCompositorBackend = 'none';
        return null;
    }
    if (!(baseTile instanceof HTMLCanvasElement) || !adjustments) {
        _lastCompositorBackend = 'none';
        return null;
    }

    const gpuOverlay = tryBuildMotionColorMatteOverlayGpu(
        baseTile,
        matteSource,
        backgroundSource,
        channels,
        adjustments
    );
    if (gpuOverlay) {
        _lastCompositorBackend = 'gpu';
        return gpuOverlay;
    }

    const cpuOverlay = buildMotionColorMatteOverlayCpu(baseTile, sources, declared, adjustments);
    _lastCompositorBackend = cpuOverlay ? 'cpu' : 'none';
    return cpuOverlay;
}
